import { useEffect } from 'react';
import styled from '@emotion/styled';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useSiteContext } from 'src/store';
import theme from 'src/themes';

declare global {
  interface window {
    rsConf: any;
    ReadSpeaker: any;
  }
}

interface IProps {
  readId: string;
}

const Container = styled('div')(() => ({
  width: '100%',
}));

const ReaderBox = styled('div')(() => ({
  maxWidth: theme.breakpoints.values.lg,
  margin: 'auto',
}));

const getLanguageCode = (locale?: string): string => {
  switch (locale?.toLowerCase()) {
    case 'en-gb':
      return 'en_uk';
    case 'en-us':
      return 'en_us';
    case 'cy':
      return 'cy_cy';
    default:
      return 'en_uk';
  }
};

const ReadSpeakerReader = ({ readId }: IProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { readSpeakerConfiguration } = useSiteContext();

  const language = getLanguageCode(router.locale);
  const isEnabled = !!(
    readSpeakerConfiguration?.enabled && readSpeakerConfiguration.customerId
  );

  useEffect(() => {
    globalThis.rsConf = {
      general: { usePost: true },
      ui: {
        tools: {
          enlarge: false,
          textmode: false,
          pagemask: false,
          download: false,
        },
      },
    };

    const onRouteChangeStart = () => {
      if (globalThis.ReadSpeaker?.ui) {
        globalThis.ReadSpeaker.ui.destroyActivePlayer();
      }
    };

    const onRouteChangeComplete = () => {
      if (globalThis.ReadSpeaker?.ui) {
        globalThis.ReadSpeaker.ui.addClickEvents();
        setTimeout(() => {
          globalThis.ReadSpeaker.init();
        }, 300);
      }
    };

    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [router.events]);

  if (!isEnabled) {
    return null;
  }

  return (
    <Container>
      <ReaderBox>
        <Script
          src={`https://cdn-eu.readspeaker.com/script/${readSpeakerConfiguration.customerId}/webReader/webReader.js?pids=wr`}
          id="rs_req_Init"
          type="text/javascript"
        />
        <section className="readspeaker-button-section mt-3 mb-2">
          <div className="rs_skip rsbtn rs_preserve">
            <a
              rel="nofollow"
              className="rsbtn_play"
              title={t('webReader.listenToThisPageText')}
              href={`https://app-eu.readspeaker.com/cgi-bin/rsent?customerid=${readSpeakerConfiguration.customerId}&lang=${language}&readid=${readId}&url=`}
            >
              <span className="rsbtn_left rsimg rspart">
                <span className="rsbtn_text">
                  <span>{t('webReader.buttonText')}</span>
                </span>
              </span>
              <span className="rsbtn_right rsimg rsplay rspart" />
            </a>
          </div>
        </section>
      </ReaderBox>
    </Container>
  );
};

export default ReadSpeakerReader;
